import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ShieldAlert, AlertTriangle, TrendingUp, Truck, Clock, Boxes, AlertOctagon, PackageMinus, Edit3, StickyNote } from 'lucide-react';
import { toast } from 'sonner';

const bandStyles = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high:     'bg-orange-100 text-orange-700 border-orange-200',
  watch:    'bg-amber-50 text-amber-700 border-amber-200',
  healthy:  'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const factorIcon = (kind) => {
  switch (kind) {
    case 'vendor':    return Truck;
    case 'cost':      return TrendingUp;
    case 'shipping':  return TrendingUp;
    case 'lead_time': return Clock;
    case 'recall':    return AlertOctagon;
    case 'stock':     return PackageMinus;
    case 'bom':       return Boxes;
    default:          return ShieldAlert;
  }
};

const ItemRiskAnalysisCard = ({ itemId }) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({
    lead_time_days: 0,
    manual_risk_note: '',
    manual_band_override: 'auto',
    vendor_delay_risk: '',
    vendor_cost_risk: '',
    vendor_geopolitical_risk: '',
  });
  const [saving, setSaving] = useState(false);

  const fetchRisk = () => {
    setLoading(true);
    api().get(`/items/${itemId}/risk-analysis`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRisk();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const openEdit = async () => {
    // Pull current vendor risk values for prefill (best-effort)
    let vendorRisk = { delay_risk: '', cost_risk: '', geopolitical_risk: '' };
    if (data?.vendor_id) {
      try {
        const r = await api().get(`/vendors/${data.vendor_id}`);
        vendorRisk = {
          delay_risk: r.data.delay_risk ?? '',
          cost_risk: r.data.cost_risk ?? '',
          geopolitical_risk: r.data.geopolitical_risk ?? '',
        };
      } catch { /* ignore */ }
    }
    setForm({
      lead_time_days: data?.lead_time_days || 0,
      manual_risk_note: data?.manual_risk_note || '',
      manual_band_override: data?.manual_band_override || 'auto',
      vendor_delay_risk: vendorRisk.delay_risk,
      vendor_cost_risk: vendorRisk.cost_risk,
      vendor_geopolitical_risk: vendorRisk.geopolitical_risk,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        lead_time_days: Number(form.lead_time_days) || 0,
        manual_risk_note: form.manual_risk_note,
        manual_band_override: form.manual_band_override === 'auto' ? '' : form.manual_band_override,
      };
      if (data?.vendor_id) {
        if (form.vendor_delay_risk !== '') payload.vendor_delay_risk = Number(form.vendor_delay_risk);
        if (form.vendor_cost_risk !== '') payload.vendor_cost_risk = Number(form.vendor_cost_risk);
        if (form.vendor_geopolitical_risk !== '') payload.vendor_geopolitical_risk = Number(form.vendor_geopolitical_risk);
      }
      await api().patch(`/items/${itemId}/risk-inputs`, payload);
      toast.success('Risk inputs updated');
      setEditOpen(false);
      fetchRisk();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <Card className="industrial-card" data-testid="item-risk-card-loading">
        <CardContent className="p-6 text-center text-sm text-slate-400">Calculating risk…</CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const band = data.risk_band || 'healthy';
  const bandCls = bandStyles[band] || bandStyles.healthy;

  return (
    <>
      <Card className="industrial-card" data-testid="item-risk-analysis-card">
        <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" /> Product Risk Analysis
          </h2>
          <div className="flex items-center gap-2">
            <span className={`text-xs uppercase tracking-wider px-2 py-0.5 border rounded-sm ${bandCls}`} data-testid="item-risk-band">
              {band} · {data.risk_score}
            </span>
            <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={openEdit} data-testid="item-risk-edit-btn">
              <Edit3 className="w-3 h-3 mr-1" /> Edit Inputs
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-5 space-y-5">
          {data.is_band_overridden && (
            <div className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center gap-2" data-testid="risk-override-banner">
              <ShieldAlert className="w-3 h-3" />
              Band manually overridden to <strong>{data.risk_band}</strong> (computed score = {data.risk_score} → {data.computed_band}).
            </div>
          )}
          {data.manual_risk_note && (
            <div className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 flex items-start gap-2" data-testid="risk-manual-note">
              <StickyNote className="w-3 h-3 mt-0.5 text-slate-500" />
              <div className="whitespace-pre-line">{data.manual_risk_note}</div>
            </div>
          )}
          {/* KPI tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-sm border border-slate-200 p-3" data-testid="risk-kpi-vendor">
              <div className="text-xs uppercase tracking-wider text-slate-500">Vendor risk idx</div>
              <div className="text-xl font-bold text-slate-900 mono">{data.vendor_risk_index ?? 0}</div>
              <div className="text-xs text-slate-500 truncate">{data.vendor_name || '—'}</div>
            </div>
            <div className="rounded-sm border border-slate-200 p-3" data-testid="risk-kpi-unit">
              <div className="text-xs uppercase tracking-wider text-slate-500">Unit cost trend</div>
              <div className={`text-xl font-bold mono ${data.unit_cost_change_pct > 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                {data.unit_cost_change_pct == null ? '—' : `${data.unit_cost_change_pct > 0 ? '+' : ''}${data.unit_cost_change_pct}%`}
              </div>
              <div className="text-xs text-slate-500">latest ${data.latest_unit_cost ?? '—'}</div>
            </div>
            <div className="rounded-sm border border-slate-200 p-3" data-testid="risk-kpi-shipping">
              <div className="text-xs uppercase tracking-wider text-slate-500">Shipping trend</div>
              <div className={`text-xl font-bold mono ${data.shipping_cost_change_pct > 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                {data.shipping_cost_change_pct == null ? '—' : `${data.shipping_cost_change_pct > 0 ? '+' : ''}${data.shipping_cost_change_pct}%`}
              </div>
              <div className="text-xs text-slate-500">latest ${data.latest_shipping_cost ?? '—'}</div>
            </div>
            <div className="rounded-sm border border-slate-200 p-3" data-testid="risk-kpi-lead-time">
              <div className="text-xs uppercase tracking-wider text-slate-500">Lead time</div>
              <div className="text-xl font-bold text-slate-900 mono">{data.lead_time_days || 0}<span className="text-sm text-slate-400 ml-1">d</span></div>
              <div className="text-xs text-slate-500">availability gauge</div>
            </div>
          </div>

          {/* Flags */}
          <div className="flex flex-wrap gap-2 text-xs" data-testid="risk-flags">
            {data.has_active_recall && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-sm border border-red-200">
                <AlertOctagon className="w-3 h-3" /> Active recall
              </span>
            )}
            {data.low_stock && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-sm border border-orange-200">
                <AlertTriangle className="w-3 h-3" /> Low stock
              </span>
            )}
            {(data.affected_bom_count || 0) > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded-sm border border-slate-200">
                <Boxes className="w-3 h-3" /> {data.affected_bom_count} BOM{data.affected_bom_count === 1 ? '' : 's'}
              </span>
            )}
            {!data.has_active_recall && !data.low_stock && (data.affected_bom_count || 0) === 0 && (
              <span className="text-slate-400">No active flags</span>
            )}
          </div>

          {/* Factors breakdown */}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Contributing factors</div>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-sm">
              {(data.factors || []).map((f, i) => {
                const Icon = factorIcon(f.kind);
                return (
                  <li key={i} className="px-3 py-2 flex items-start gap-3" data-testid={`risk-factor-${f.kind}`}>
                    <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-800 font-medium">{f.label}</div>
                      <div className="text-xs text-slate-500">{f.detail}</div>
                    </div>
                    <div className="text-xs text-slate-500 mono shrink-0">w {f.weight}</div>
                  </li>
                );
              })}
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Edit Inputs Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="risk-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Risk Inputs — {data.item_sku}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Lead Time (days)</Label>
              <Input
                type="number" min={0}
                value={form.lead_time_days}
                onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })}
                data-testid="risk-edit-lead-time"
              />
              <p className="text-[11px] text-slate-400">Higher lead time = more risk score. Capped at 30 pts contribution.</p>
            </div>

            {data.vendor_id && (
              <div className="border border-slate-200 rounded-sm p-3 space-y-3 bg-slate-50">
                <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-2">
                  <Truck className="w-3 h-3" /> Vendor risk levels — {data.vendor_name}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    ['vendor_delay_risk', 'Delay', 'risk-edit-vendor-delay', 'How often does this vendor miss promised dates?'],
                    ['vendor_cost_risk', 'Cost', 'risk-edit-vendor-cost', 'How volatile or unpredictable is the unit cost?'],
                    ['vendor_geopolitical_risk', 'Geo', 'risk-edit-vendor-geo', 'Country / trade-route exposure (tariffs, conflict)'],
                  ].map(([k, lbl, tid, hint]) => {
                    const v = form[k] === '' ? 0 : Number(form[k]);
                    const tone = v >= 4 ? 'text-red-600' : v >= 3 ? 'text-amber-600' : v >= 2 ? 'text-yellow-600' : 'text-emerald-600';
                    return (
                      <div key={k} className="space-y-1">
                        <Label className="text-xs flex items-center justify-between">
                          <span>{lbl} (1–5)</span>
                          <span className={`mono font-bold text-sm ${tone}`} data-testid={`${tid}-value`}>{v || '—'}</span>
                        </Label>
                        <input type="range" min={0} max={5} step={1} value={v}
                          onChange={(e) => setForm({ ...form, [k]: e.target.value === '0' ? '' : e.target.value })}
                          className="w-full accent-orange-600 cursor-pointer"
                          data-testid={tid}
                          title={hint} />
                        <div className="flex justify-between text-[9px] text-slate-400 mono">
                          <span>none</span><span>1·low</span><span>3</span><span>5·high</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-1">
              <Label>Manual Band Override</Label>
              <Select value={form.manual_band_override} onValueChange={(v) => setForm({ ...form, manual_band_override: v })}>
                <SelectTrigger data-testid="risk-edit-band-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (use computed score)</SelectItem>
                  <SelectItem value="critical">Force Critical</SelectItem>
                  <SelectItem value="high">Force High</SelectItem>
                  <SelectItem value="watch">Force Watch</SelectItem>
                  <SelectItem value="healthy">Force Healthy</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400">Use when operator knowledge contradicts the data (audits, supply-chain news, etc.).</p>
            </div>

            <div className="space-y-1">
              <Label>Operator Note</Label>
              <Textarea
                rows={3}
                value={form.manual_risk_note}
                onChange={(e) => setForm({ ...form, manual_risk_note: e.target.value })}
                placeholder="e.g., Vendor warehouse moving Mar 15. Expect 2-week shipping delays."
                data-testid="risk-edit-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-orange-500 hover:bg-orange-600 rounded-sm" data-testid="risk-edit-save-btn">
              {saving ? 'Saving…' : 'Save Risk Inputs'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ItemRiskAnalysisCard;
